import { useState, useMemo, useCallback } from 'react';
import { JournalEntry } from '../types/accounting';

export interface EnhancedJournalFilters {
  search: string;
  journalType: string;
  startDate: string;
  endDate: string;
  status: string;
  source: string;
}

const initialFilters: EnhancedJournalFilters = {
  search: '',
  journalType: '',
  startDate: '',
  endDate: '',
  status: '',
  source: ''
};

export function useEnhancedJournalFilters(entries: JournalEntry[]) {
  const [filters, setFilters] = useState<EnhancedJournalFilters>(initialFilters);

  const updateFilters = useCallback((newFilters: Partial<EnhancedJournalFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  // S'assurer que entries est toujours un tableau
  const safeEntries = Array.isArray(entries) ? entries : [];

  const filteredEntries = useMemo(() => {
    if (!safeEntries || safeEntries.length === 0) return [];

    return safeEntries.filter(entry => {
      // Recherche textuelle dans plusieurs champs
      if (filters.search) {
        const searchLower = filters.search.toLowerCase().trim();
        if (searchLower) {
          const matchesSearch = 
            entry.reference.toLowerCase().includes(searchLower) ||
            entry.description.toLowerCase().includes(searchLower) ||
            entry.lines?.some(line => 
              line.accountCode?.toLowerCase().includes(searchLower) ||
              line.accountName?.toLowerCase().includes(searchLower) ||
              line.description?.toLowerCase().includes(searchLower)
            ) ||
            false;
          
          if (!matchesSearch) return false;
        }
      }

      // Filtre par type de journal
      if (filters.journalType && filters.journalType !== '' && filters.journalType !== 'all') {
        if (entry.journalType !== filters.journalType) return false;
      }

      // Filtre par statut
      if (filters.status && filters.status !== '' && filters.status !== 'all') {
        if (entry.status !== filters.status) return false;
      }

      // Filtre par source
      if (filters.source && filters.source !== '' && filters.source !== 'all') {
        const entrySource = entry.source || 'manual'; // Default to manual if undefined
        if (entrySource !== filters.source) return false;
      }

      // Filtre par date de début
      if (filters.startDate) {
        const entryDate = new Date(entry.date);
        const startDate = new Date(filters.startDate);
        if (entryDate < startDate) return false;
      }

      // Filtre par date de fin
      if (filters.endDate) {
        const entryDate = new Date(entry.date);
        const endDate = new Date(filters.endDate);
        if (entryDate > endDate) return false;
      }

      return true;
    });
  }, [safeEntries, filters]);

  const isFiltersActive = useMemo(() => {
    return !!(
      filters.search || 
      (filters.journalType && filters.journalType !== '' && filters.journalType !== 'all') || 
      filters.startDate || 
      filters.endDate || 
      (filters.status && filters.status !== '' && filters.status !== 'all') || 
      (filters.source && filters.source !== '' && filters.source !== 'all')
    );
  }, [filters]);

  const filterStats = useMemo(() => {
    const totalEntries = safeEntries.length;
    const filteredCount = filteredEntries.length;
    const totalDebit = filteredEntries.reduce((sum, entry) => sum + (entry.totalDebit || 0), 0);
    const totalCredit = filteredEntries.reduce((sum, entry) => sum + (entry.totalCredit || 0), 0);

    return {
      totalEntries,
      filteredCount,
      filteredPercentage: totalEntries > 0 ? Math.round((filteredCount / totalEntries) * 100) : 0,
      totalDebit,
      totalCredit,
      isBalanced: totalDebit === totalCredit
    };
  }, [safeEntries, filteredEntries]);

  // Fonctions utilitaires pour les options de filtre
  const getJournalTypeOptions = useMemo(() => {
    return [
      { value: '', label: 'Tous les types' },
      { value: 'sales', label: 'Ventes' },
      { value: 'purchases', label: 'Achats' },
      { value: 'bank', label: 'Banque' },
      { value: 'cash', label: 'Caisse' },
      { value: 'general', label: 'OD Diverses' }
    ];
  }, []);

  const getStatusOptions = useMemo(() => {
    return [
      { value: '', label: 'Tous les statuts' },
      { value: 'draft', label: 'Brouillon' },
      { value: 'posted', label: 'Validé' },
      { value: 'cancelled', label: 'Annulé' }
    ];
  }, []);

  const getSourceOptions = useMemo(() => {
    return [
      { value: '', label: 'Toutes les sources' },
      { value: 'manual', label: 'Saisie manuelle' },
      { value: 'agent', label: 'ADHA AI' },
      { value: 'import', label: 'Import' }
    ];
  }, []);

  return {
    filters,
    updateFilters,
    resetFilters,
    filteredEntries,
    isFiltersActive,
    filterStats,
    getJournalTypeOptions,
    getStatusOptions,
    getSourceOptions
  };
}
